import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { SelectProps } from "antd";
import { Button, Select, Space } from "antd";
import type { ReactNode } from "react";

interface ChannelOption<ValueType extends string | number> {
  label: ReactNode;
  value: ValueType;
}

interface ChannelSelectorProps<ValueType extends string | number>
  extends Omit<SelectProps<ValueType>, "options"> {
  options: ChannelOption<ValueType>[];
}

type OptionType<ValueType extends string | number> = NonNullable<
  SelectProps<ValueType>["options"]
>[number];

const ChannelSelector = <ValueType extends string | number = number>({
  options,
  value,
  onChange,
  style,
  ...rest
}: ChannelSelectorProps<ValueType>) => {
  const currentIndex = options.findIndex((option) => option.value === value);

  const move = (direction: "prev" | "next") => {
    if (options.length === 0 || !onChange) {
      return;
    }
    let nextIndex = 0;
    if (currentIndex === -1) {
      nextIndex = 0;
    } else if (direction === "prev") {
      nextIndex = currentIndex === 0 ? options.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === options.length - 1 ? 0 : currentIndex + 1;
    }
    const nextValue = options[nextIndex];
    onChange(nextValue.value, nextValue as OptionType<ValueType>);
  };

  return (
    <Space.Compact style={{ minWidth: 280, ...style }}>
      <Button
        icon={<LeftOutlined />}
        onClick={() => move("prev")}
        disabled={options.length === 0}
      />
      <Select<ValueType>
        {...rest}
        style={{ minWidth: 0 }}
        options={options}
        value={value}
        onChange={onChange}
        optionFilterProp="label"
      />
      <Button
        icon={<RightOutlined />}
        onClick={() => move("next")}
        disabled={options.length === 0}
      />
    </Space.Compact>
  );
};

export default ChannelSelector;
